import type { Entity } from '@backstage/catalog-model';

import { List } from '@backstage/ui';
import clsx from 'clsx';
import { useLayoutEffect, useRef } from 'react';

import { HomeCardEntityItem } from './HomeCardEntityItem';
import classes from './HomeCardEntityList.module.css';

type Props = {
  'aria-label': string;
  'entities': Entity[];
  'loading'?: boolean;
  'message'?: string;
  'onPageSizeChange': (pageSize: number) => void;
  'pageSize': number;
  'showImages'?: boolean;
};

export const HomeCardEntityList = ({
  'aria-label': ariaLabel,
  entities,
  loading = false,
  message,
  onPageSizeChange,
  pageSize,
  showImages = true,
}: Props) => {
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = listRef.current;
    if (!element) return;

    const measure = () => {
      const style = getComputedStyle(element);
      const columns = style.gridTemplateColumns.split(/\s+/).filter(Boolean);
      const rowHeight = Number(style.gridAutoRows.replace(/px$/, ''));
      const gap = Number(style.rowGap.replace(/px$/, ''));
      const height = element.getBoundingClientRect().height;
      if (height === 0 || !Number.isFinite(rowHeight) || !Number.isFinite(gap)) return;

      const rows = Math.floor((height + gap) / (rowHeight + gap));
      onPageSizeChange(columns.length * rows);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [onPageSizeChange, showImages]);

  return (
    <div className={clsx(classes.root, !showImages && classes.withoutImages)}>
      <div aria-hidden="true" className={classes.measure} ref={listRef} />
      {message
        ? (
            <div className={classes.message} role="status">
              <p>{message}</p>
            </div>
          )
        : (
            <List aria-label={ariaLabel} className={classes.list}>
              {loading
                ? Array.from({ length: pageSize }, (_, index) => (
                    <HomeCardEntityItem key={index} loading showImages={showImages} />
                  ))
                : entities.map(entity => (
                    <HomeCardEntityItem entity={entity} key={entity.metadata.uid} showImages={showImages} />
                  ))}
            </List>
          )}
    </div>
  );
};
